// src/pages/couriers/components/RuleCard.jsx
import React from 'react';
import { Check, X, Star, Copy, Edit, Trash2 } from 'lucide-react';
import { usePermissions } from '../../../hooks/usePermissions';
import { useLocalization } from '../../../contexts/LocalizationContext';
import { formatDate } from '../../../utils/dateUtils';
import RuleConfigPreview from './RuleConfigPreview';
import RuleDetails from './RuleDetails';
import styles from './RuleCard.module.css';

const RuleCard = ({
  rule,
  isExpanded,
  onToggleExpansion,
  onToggleRule,
  onToggleDefault,
  onDuplicate,
  onEdit,
  onDelete,
  showCourierName,
  cities,
  districts,
  provinces,
  shippingZones,
  shops
}) => {
  const { hasPermission } = usePermissions();
  const { t } = useLocalization();

  return (
    <div className={styles.ruleCard}>
      <div className={styles.ruleCardHeader}>
        <div className={styles.ruleNameSection}>
          <div className={styles.ruleNameRow}>
            <h3>{rule.name}</h3>
            {rule.isDefault && (
              <span className={styles.defaultBadge}>
                <Star size={12} />
                {t('couriers.defaultFirst')}
              </span>
            )}
          </div>
          {showCourierName && <div className={styles.courierName}>{rule.CourierCompany?.name}</div>}
          {rule.description && <p className={styles.ruleDescription}>{rule.description}</p>}
        </div>
        <div className={styles.ruleActions}>
          {hasPermission('manage_courier_assignment_rules') && (
            <button
              onClick={() => onToggleDefault(rule)}
              className={`${styles.iconButton} ${rule.isDefault ? styles.defaultButton : ''}`}
              title={rule.isDefault ? t('couriers.unsetAsDefault') : t('couriers.setAsDefault')}
            >
              <Star size={16} />
            </button>
          )}
          <div className={styles.toggleWrapper}>
            <input
              type="checkbox"
              checked={rule.isActive}
              onChange={() => onToggleRule(rule.id, rule.isActive)}
              className={styles.toggleInput}
              disabled={!hasPermission('manage_courier_assignment_rules')}
            />
            <span className={styles.toggleSlider}></span>
          </div>
          {hasPermission('manage_courier_assignment_rules') && (
            <>
              <button onClick={() => onDuplicate(rule)} className={styles.iconButton} title={t('couriers.duplicate')}>
                <Copy size={16} />
              </button>
              <button onClick={() => onEdit(rule)} className={styles.iconButton} title={t('couriers.editRule')}>
                <Edit size={16} />
              </button>
              <button
                onClick={() => onDelete(rule)}
                className={`${styles.iconButton} ${styles.dangerIcon}`}
                title={t('couriers.deleteRule')}
              >
                <Trash2 size={16} />
              </button>
            </>
          )}
        </div>
      </div>
      <div className={styles.ruleCardBody}>
        <div className={styles.statusRow}>
          <span className={`${styles.badge} ${rule.isActive ? styles.activeBadge : styles.inactiveBadge}`}>
            {rule.isActive ? (
              <>
                <Check size={12} />
                {t('couriers.active')}
              </>
            ) : (
              <>
                <X size={12} />
                {t('couriers.inactiveStatus')}
              </>
            )}
          </span>
        </div>
        <div className={styles.ruleConfigSection}>
          <RuleConfigPreview
            rules={rule.rules}
            isExpanded={isExpanded}
            onToggleExpansion={() => onToggleExpansion(rule.id)}
          />
          {isExpanded && (
            <div className={styles.ruleConfigDetails}>
              <RuleDetails
                rules={rule.rules}
                cities={cities}
                districts={districts}
                provinces={provinces}
                shippingZones={shippingZones}
                shops={shops}
              />
            </div>
          )}
        </div>
      </div>
      <div className={styles.ruleCardFooter}>
        <div className={styles.metaInfo}>
          <span>{t('couriers.createdBy')} {rule.createdBy?.name || t('couriers.unknown')}</span>
          <span>•</span>
          <span>{formatDate(rule.createdAt)}</span>
          {rule.updatedBy && (
            <>
              <span>•</span>
              <span>{t('couriers.updatedBy')} {rule.updatedBy.name}</span>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default RuleCard;